import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { SUPPORT_MODE_PERMISSIONS } from '../../../shared/data/config/constants-buttons';
import { Profile } from '../../interfaces/permision.interface';
import { selectAuth } from '../../state/auth/auth.selector';
import { hasPermissionFromRole } from '../../utils/permission.util';

const SUPPORT_MODE_STORAGE_KEY = 'supportMode';

@Injectable({ providedIn: 'root' })
export class SupportModeService {
  private readonly requestedSubject = new BehaviorSubject<boolean>(
    this.readStoredPreference(),
  );

  public readonly isAllowed$: Observable<boolean> = this.store
    .select(selectAuth)
    .pipe(
      map((profile) =>
        hasPermissionFromRole(
          (profile as Partial<Profile>)?.rol,
          SUPPORT_MODE_PERMISSIONS.TOGGLE_SUPPORT_MODE,
        ),
      ),
    );

  public readonly isEnabled$: Observable<boolean> = combineLatest([
    this.requestedSubject.asObservable(),
    this.isAllowed$,
  ]).pipe(map(([requested, isAllowed]) => requested && isAllowed));

  constructor(private store: Store) {}

  public toggle(): void {
    this.setEnabled(!this.requestedSubject.value);
  }

  public setEnabled(enabled: boolean): void {
    this.requestedSubject.next(enabled);
    this.writeStoredPreference(enabled);
  }

  private readStoredPreference(): boolean {
    try {
      return sessionStorage.getItem(SUPPORT_MODE_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private writeStoredPreference(enabled: boolean): void {
    try {
      sessionStorage.setItem(SUPPORT_MODE_STORAGE_KEY, String(enabled));
    } catch {
      return;
    }
  }
}
